import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Svgs } from '../assets/svg';

type ItemId = string | number;

const spacingPresets = {
  flush: {
    list: { py: 0, pt: '3px' },
    item: { py: 0 },
    button: { px: '10px', py: 1, mx: '-10px' },
  },
  standard: {
    list: { py: 1 },
    item: { py: 0 },
    button: { px: 2, py: 1 },
  },
};

interface ClickableListProps<T> {
  items: T[];
  initialSelectedId?: ItemId;
  renderItem: (item: T) => ReactNode;
  getItemKey: (item: T) => any;
  onItemClick: (val: any) => Promise<boolean>;
  divider?: boolean;
  spacing?: 'flush' | 'standard';
  selectionIndicator?: 'checkmark' | 'none';
  checkmarkIcon?: ReactNode;
}

function ClickableList<T>({
  items,
  initialSelectedId,
  renderItem,
  getItemKey,
  onItemClick,
  divider = false,
  spacing = 'standard',
  selectionIndicator = 'none',
  checkmarkIcon,
}: ClickableListProps<T>) {
  const preset = spacingPresets[spacing];
  const [selectedItemId, setSelectedItemId] = useState(initialSelectedId);
  const [loadingItemId, setLoadingItemId] = useState<ItemId | null>(null);
  const showSelectionIndicator = selectionIndicator !== 'none';

  useEffect(() => {
    setSelectedItemId(initialSelectedId);
  }, [initialSelectedId]);

  const handleItemClick = useCallback(
    async (itemId: ItemId) => {
      if (loadingItemId !== null) return;

      setLoadingItemId(itemId);
      try {
        const success = await onItemClick(itemId);
        if (success) {
          setSelectedItemId(itemId);
        }
      } catch (error) {
        console.error('Error selecting item:', error);
      } finally {
        setLoadingItemId(null);
      }
    },
    [loadingItemId, onItemClick],
  );

  return (
    <List sx={{ width: '100%', ...preset.list }}>
      {items.map(item => {
        const itemId = getItemKey(item);
        const isSelected = selectedItemId === itemId;
        const isLoading = loadingItemId === itemId;

        return (
          <ListItem key={itemId} className='clickableListItem' divider={divider} sx={preset.item}>
            <ListItemButton onClick={() => handleItemClick(itemId)} sx={preset.button}>
              <ListItemText
                primary={
                  <Typography
                    variant='p16'
                    color='secondary.dark'
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    {renderItem(item)}
                    {showSelectionIndicator &&
                      isSelected &&
                      !isLoading &&
                      (checkmarkIcon ?? <Svgs.IconCheckmark />)}
                    {showSelectionIndicator && isLoading && <CircularProgress size={20} />}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

export default ClickableList;
